import { useCallback, useEffect, useState } from "react";
import {
  createOrganizationDomain,
  deleteOrganizationDomain,
  listOrganizationDomains,
  lookupOrganizationDomainCandidates,
  updateOrganizationDomain,
} from "services/organizationDomains";
import {
  OrganizationDomain,
  OrganizationDomainCandidate,
} from "types/organizationDomain";

function errorMessage(error: any): string {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong. Please try again."
  );
}

export function useOrganizationDomains() {
  const [domains, setDomains] = useState<OrganizationDomain[]>([]);
  const [candidates, setCandidates] = useState<OrganizationDomainCandidate[]>(
    [],
  );
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [lookingUp, setLookingUp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLookedUp, setHasLookedUp] = useState(false);

  const refreshDomains = useCallback(async () => {
    setLoadingDomains(true);
    try {
      setDomains(await listOrganizationDomains());
      setError(null);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoadingDomains(false);
    }
  }, []);

  useEffect(() => {
    refreshDomains();
  }, [refreshDomains]);

  const lookup = async (name?: string) => {
    setLookingUp(true);
    setHasLookedUp(true);
    try {
      setCandidates(await lookupOrganizationDomainCandidates(name));
      setError(null);
    } catch (requestError) {
      setCandidates([]);
      setError(errorMessage(requestError));
    } finally {
      setLookingUp(false);
    }
  };

  const mutate = async (request: () => Promise<unknown>) => {
    setSaving(true);
    try {
      await request();
      setError(null);
      await refreshDomains();
      return true;
    } catch (requestError) {
      setError(errorMessage(requestError));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const createDomain = async (
    name: string,
    orgIds: string[],
    candidatePrefix?: string,
  ) => {
    const created = await mutate(() => createOrganizationDomain(name, orgIds));
    if (created && candidatePrefix) {
      setCandidates((current) =>
        current.filter((candidate) => candidate.prefix !== candidatePrefix),
      );
    }
    return created;
  };

  return {
    domains,
    candidates,
    loadingDomains,
    lookingUp,
    saving,
    error,
    hasLookedUp,
    lookup,
    createDomain,
    updateDomain: (id: string, name: string, orgIds: string[]) =>
      mutate(() => updateOrganizationDomain(id, name, orgIds)),
    deleteDomain: (id: string) => mutate(() => deleteOrganizationDomain(id)),
  };
}
